import { useState } from "react";
import { Platform, View } from "react-native";
import Constants from "expo-constants";
import { useRouter } from "expo-router";
import {
  TICKET_LIMITS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
  TICKET_TYPE_OPTIONS,
  type TicketStatus,
  type TicketType,
} from "@brad-tipper/app-frontend/tickets";

import {
  Badge,
  Button,
  Card,
  Input,
  Screen,
  Text,
  useTheme,
} from "~/components/ui";
import { useAuth } from "~/lib/auth";
import { useCreateTicket, useTickets } from "~/hooks/useTickets";
import { spacing } from "~/styles/tokens";

const statusVariant = (status: TicketStatus) =>
  status === "resolved"
    ? "success"
    : status === "in_progress" || status === "planned"
      ? "warning"
      : "default";

export default function SupportScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const { user } = useAuth();
  const [filter, setFilter] = useState<"open" | "closed">("open");
  const [composing, setComposing] = useState(false);
  const [type, setType] = useState<TicketType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const tickets = useTickets(filter, { enabled: Boolean(user) });
  const create = useCreateTicket();

  const valid = title.trim().length >= 3 && description.trim().length >= 10;

  async function submit() {
    if (!valid) return;
    const ticket = await create.mutateAsync({
      type,
      title: title.trim(),
      description: description.trim(),
      appVersion: Constants.expoConfig?.version,
      platform: Platform.OS,
    });
    setTitle("");
    setDescription("");
    setComposing(false);
    router.push(`/support/${ticket.id}` as never);
  }

  return (
    <Screen scroll contentStyle={{ gap: spacing.md, paddingTop: spacing.lg }}>
      <Text variant="heading">Support</Text>
      <Text variant="body" color={colors.muted}>
        Report a bug, ask for a feature or leave feedback. Follow it here and
        add notes any time.
      </Text>

      {!user ? (
        <Text variant="body">
          Sign in to send us a ticket and follow the replies.
        </Text>
      ) : (
        <>
          {composing ? (
            <Card elevation="raised">
              <View style={{ gap: spacing.md }}>
                <Text variant="label">New ticket</Text>
                <View style={{ flexDirection: "row", gap: spacing.sm }}>
                  {TICKET_TYPE_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      label={option.label}
                      size="sm"
                      variant={type === option.value ? "primary" : "secondary"}
                      onPress={() => setType(option.value)}
                    />
                  ))}
                </View>
                <Input
                  label="Title"
                  value={title}
                  onChangeText={setTitle}
                  placeholder="A short summary"
                />
                <Input
                  label="Details"
                  value={description}
                  onChangeText={setDescription}
                  multiline
                  numberOfLines={6}
                  placeholder={
                    type === "bug"
                      ? "What happened, and what did you expect?"
                      : "Tell us more"
                  }
                  hint={`Up to ${TICKET_LIMITS.description} characters.`}
                />
                {create.error ? (
                  <Text variant="caption" color={colors.danger}>
                    {create.error instanceof Error
                      ? create.error.message
                      : "Unable to send."}
                  </Text>
                ) : null}
                <Button
                  label="Send"
                  loading={create.isPending}
                  disabled={!valid}
                  onPress={() => void submit()}
                  fullWidth
                />
                <Button
                  label="Cancel"
                  variant="ghost"
                  onPress={() => setComposing(false)}
                  fullWidth
                />
              </View>
            </Card>
          ) : (
            <Button
              label="New ticket"
              onPress={() => setComposing(true)}
              fullWidth
            />
          )}

          <View style={{ flexDirection: "row", gap: spacing.sm }}>
            <Button
              label="Open"
              size="sm"
              variant={filter === "open" ? "primary" : "secondary"}
              onPress={() => setFilter("open")}
            />
            <Button
              label="Closed"
              size="sm"
              variant={filter === "closed" ? "primary" : "secondary"}
              onPress={() => setFilter("closed")}
            />
          </View>

          {tickets.isLoading ? (
            <Text variant="body" color={colors.muted}>
              Loading…
            </Text>
          ) : tickets.error ? (
            <Text variant="body" color={colors.danger}>
              Unable to load your tickets.
            </Text>
          ) : tickets.data && tickets.data.length > 0 ? (
            tickets.data.map((ticket) => (
              <Card key={ticket.id} elevation="raised">
                <View style={{ gap: spacing.sm }}>
                  <View style={{ flexDirection: "row", gap: spacing.sm }}>
                    <Badge
                      label={TICKET_STATUS_LABELS[ticket.status]}
                      variant={statusVariant(ticket.status)}
                    />
                    <Text variant="caption" color={colors.muted}>
                      {TICKET_TYPE_LABELS[ticket.type]} · updated{" "}
                      {new Date(ticket.updatedAt).toLocaleDateString()}
                    </Text>
                  </View>
                  <Button
                    label={`#${ticket.number} ${ticket.title}`}
                    variant="ghost"
                    style={{
                      justifyContent: "flex-start",
                      paddingHorizontal: 0,
                    }}
                    accessibilityLabel={`Open ticket ${ticket.number}`}
                    onPress={() =>
                      router.push(`/support/${ticket.id}` as never)
                    }
                  />
                </View>
              </Card>
            ))
          ) : (
            <Text variant="body" color={colors.muted}>
              {filter === "open" ? "No open tickets." : "No closed tickets."}
            </Text>
          )}
        </>
      )}
    </Screen>
  );
}
